<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useRoute, useRouter } from 'vue-router'
import { useInvestingInvestorStore } from '@/modules/investing/stores/investor-store'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import { useCandidateStore } from '@/modules/connecting/stores/candidate-store'
import { useUnivesrityStore } from '@/modules/shared/stores/university-store'
import { useAccreditationStore } from '@/modules/screening/stores/accreditation-store'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'

import VShimmerLogo from '@/modules/connecting/components/v-shimmer-logo.vue'
import OnboardingAccountForm from '@/modules/connecting/components/individuals/onboarding-account-form.vue'
import OnboardingAccreditationForm from '@/modules/connecting/components/individuals/onboarding-accreditation-form.vue'
import OnboardingInvestmentForm from '@/modules/connecting/components/individuals/onboarding-investment-form.vue'
import OnboardingIndustriesForm from '@/modules/connecting/components/individuals/onboarding-industries-form.vue'
import OnboardingUniversityForm from '@/modules/connecting/components/individuals/onboarding-university-form.vue'
import OnboardingDisclosuresForm from '@/modules/connecting/components/individuals/onboarding-disclosures-form.vue'
import OnboardingReview from '@/modules/connecting/components/individuals/onboarding-review.vue'
import OnboardingSuccess from '../../components/individuals/onboarding-success.vue'
import { VNotification } from '@/modules/shared/components'
import { useOnboarding, type OnboardingStep } from '@/modules/shared/composables/use-onboarding'
import OnboardingShell from '@/modules/shared/components/onboarding/OnboardingShell.vue'

const slug = 'connections'
const route = useRoute()
const router = useRouter()

const candidateStore = useCandidateStore()
const investorStore = useInvestingInvestorStore()
const individualStore = useIndividualStore()
const accreditationStore = useAccreditationStore()
const universityStore = useUnivesrityStore()
const notificationStore = useNotificationStore()
const currentIndividual = ref<any>(null)

///////////////////////////////////////////////////////////////////////////////
// Steps
///////////////////////////////////////////////////////////////////////////////

const STEPS: OnboardingStep[] = [
  {
    key: 'profile',
    icon: 'User',
    title: 'Tell us about yourself',
    subtitle: 'Confirm your details to get started.',
  },
  {
    key: 'accreditation',
    icon: 'ShieldCheck',
    title: "What's your investor accreditation?",
    subtitle: 'This helps us match you with eligible opportunities.',
  },
  {
    key: 'investment',
    icon: 'TrendingUp',
    title: 'How do you like to invest?',
    subtitle: "We'll match you with firms that fit your style.",
  },
  {
    key: 'industries',
    icon: 'Grid2x2',
    title: 'Which industries excite you?',
    subtitle: "We'll prioritize firms in the sectors you care about.",
  },
  {
    key: 'university',
    icon: 'GraduationCap',
    title: 'Any alumni connections?',
    subtitle: 'Add your universities to connect with firms in your alumni network.',
  },
  {
    key: 'disclosures',
    icon: 'FileLines',
    title: 'A few important things...',
    subtitle: 'Please review and acknowledge these risk disclosures before continuing.',
  },
  {
    key: 'review',
    icon: 'CheckCircle',
    title: 'Does everything look right?',
    subtitle: 'Tap any row to jump back and make changes.',
  },
]

// Map backend step → first frontend step index for that phase
const BACKEND_STEP_TO_FRONTEND: Record<string, number> = {
  personal_information: 0,
  accreditation: 1,
  connecting_candidate: 2,
  connecting_industries: 3,
  connecting_university: 4,
  disclosures: 5,
  review: 6,
}

const isEditMode = computed(() => route.name === 'connecting.individuals.edit')
const showSuccess = ref(false)

///////////////////////////////////////////////////////////////////////////////
// Data
///////////////////////////////////////////////////////////////////////////////

const { individualId } = route.params as { individualId: string }
const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const fetchData = async () => {
  await investorStore.fetchIndividual(individualId)
  currentIndividual.value = investorStore.items.get(`individual:${individualId}`)

  if (!currentIndividual.value.connecting_candidate_id) return
  await candidateStore.fetchCandidate(currentIndividual.value.connecting_candidate_id)
}

const incrementConnectionsOnboardingStep = async (step: string) => {
  await individualStore.incrementConnectionsOnboardingStep(individualId, step)
}

///////////////////////////////////////////////////////////////////////////////
// Candidate state (held across steps 2-4, saved at step 4)
///////////////////////////////////////////////////////////////////////////////

const candidateData = reactive({
  investment_types_interested: [] as string[],
  fund_investment_range: null as string | null,
  spv_investment_range: null as string | null,
  deal_investment_range: null as string | null,
  industries_interested: [] as string[],
  university_affiliations: [] as string[],
})

const disclosuresAgreed = ref(false)

const isNewCandidate = computed(() => !currentIndividual.value?.connecting_candidate_id)
const candidate = computed(() => candidateStore.getCandidate(currentIndividual.value?.connecting_candidate_id))

const initCandidateData = () => {
  const c = candidate.value
  if (!c) return
  candidateData.investment_types_interested = c.investment_types_interested || []
  candidateData.fund_investment_range = c.fund_investment_range || null
  candidateData.spv_investment_range = c.spv_investment_range || null
  candidateData.deal_investment_range = c.deal_investment_range || null
  candidateData.industries_interested = c.industries_interested || []
  candidateData.university_affiliations = c.university_affiliations || []
}

///////////////////////////////////////////////////////////////////////////////
// Accreditation label (for review)
///////////////////////////////////////////////////////////////////////////////

const accreditationLabel = computed(() => {
  const acc = currentIndividual.value?.accreditation
  if (!acc?.response_id || !acc?.template) return ''
  for (const section of acc.template) {
    const match = section.options?.find((o: any) => o.id === acc.response_id)
    if (match) return match.option
  }
  return ''
})

///////////////////////////////////////////////////////////////////////////////
// Step Submissions (optimistic — UI advances immediately, saves run in background)
///////////////////////////////////////////////////////////////////////////////

const onSubmitPersonalInformation = (payload: any) => {
  individualStore
    .updatePersonalInformation(individualId, payload, { slug })
    .then(() => incrementConnectionsOnboardingStep('personal_information'))
    .then(() => fetchData())
    .catch(() => notificationStore.enqueue('error', "Your profile couldn't be saved. Please go back and try again."))
}

const onSubmitAccreditation = (payload: any) => {
  accreditationStore
    .updateResponse(currentIndividual.value.accreditation.id, payload, { slug })
    .then(() => incrementConnectionsOnboardingStep('accreditation'))
    .then(() => fetchData())
    .then(() => {
      const step = currentIndividual.value?.connections_onboarding_current_step
      if (step === 'connecting_candidate' || step === 'disclosures') {
        onboarding.goToStep(BACKEND_STEP_TO_FRONTEND[step] ?? onboarding.currentStep.value)
      }
    })
    .catch(() =>
      notificationStore.enqueue('error', "Your accreditation couldn't be saved. Please go back and try again."),
    )
}

const saveCandidate = async (backendStep: string) => {
  const payload: any = {
    investment_types_interested: candidateData.investment_types_interested,
    fund_investment_range: candidateData.investment_types_interested.includes('fund')
      ? candidateData.fund_investment_range
      : null,
    spv_investment_range: candidateData.investment_types_interested.includes('syndicate')
      ? candidateData.spv_investment_range
      : null,
    deal_investment_range: candidateData.investment_types_interested.includes('angel_group')
      ? candidateData.deal_investment_range
      : null,
    industries_interested: candidateData.industries_interested,
    industries_excluded: [],
    university_affiliations: candidateData.university_affiliations,
  }

  if (isNewCandidate.value) {
    payload.subject_id = individualId
    payload.subject_type = 'Investor'
    await candidateStore.createCandidate(payload)
  } else {
    payload.id = candidate.value?.id
    await candidateStore.updateCandidate(payload)
  }

  await incrementConnectionsOnboardingStep(backendStep)
  await fetchData()
}

// Sequential queue for candidate saves (create must finish before updates)
let candidateSaveQueue: Promise<void> = Promise.resolve()

const queueCandidateSave = (backendStep: string) => {
  candidateSaveQueue = candidateSaveQueue
    .then(() => saveCandidate(backendStep))
    .catch(() =>
      notificationStore.enqueue('error', "Your preferences couldn't be saved. Please go back and try again."),
    )
}

const submitOnboarding = async () => {
  await candidateSaveQueue
  if (isEditMode.value) {
    router.push({ name: 'connecting.individuals' })
  } else {
    await incrementConnectionsOnboardingStep('review')
    showSuccess.value = true
  }
}

///////////////////////////////////////////////////////////////////////////////
// Form Refs
///////////////////////////////////////////////////////////////////////////////

const accountForm = ref<InstanceType<typeof OnboardingAccountForm> | null>(null)
const accreditationForm = ref<InstanceType<typeof OnboardingAccreditationForm> | null>(null)
const investmentForm = ref<InstanceType<typeof OnboardingInvestmentForm> | null>(null)
const disclosuresForm = ref<InstanceType<typeof OnboardingDisclosuresForm> | null>(null)

///////////////////////////////////////////////////////////////////////////////
// Wizard (shared composable)
///////////////////////////////////////////////////////////////////////////////

const validateStep = async (stepIndex: number): Promise<boolean> => {
  const step = STEPS[stepIndex]
  switch (step.key) {
    case 'profile':
      return (await accountForm.value?.isValid()) ?? true
    case 'accreditation':
      return (await accreditationForm.value?.isValid()) ?? true
    case 'investment':
      return (await investmentForm.value?.isValid()) ?? true
    case 'disclosures':
      return (await disclosuresForm.value?.isValid()) ?? true
    default:
      return true
  }
}

const onBeforeAdvance = (stepIndex: number) => {
  const step = STEPS[stepIndex]
  switch (step.key) {
    case 'profile':
      accountForm.value?.submit()
      return
    case 'accreditation':
      accreditationForm.value?.submit()
      return
    case 'investment':
      queueCandidateSave('connecting_candidate')
      return
    case 'industries':
      queueCandidateSave('connecting_industries')
      return
    case 'university':
      queueCandidateSave('connecting_university')
      return
    case 'disclosures':
      incrementConnectionsOnboardingStep('disclosures')
      return
  }
}

const onboarding = useOnboarding({
  steps: STEPS,
  startAtReview: isEditMode.value,
  scrollSelector: '.onboarding-scroll',
  validateStep,
  onBeforeAdvance,
})

///////////////////////////////////////////////////////////////////////////////
// Mount
///////////////////////////////////////////////////////////////////////////////

onMounted(async () => {
  if (route.query.success) {
    showSuccess.value = true
    return
  }

  showSkeleton()
  await Promise.all([universityStore.fetchUSUniversities(), fetchData()])

  if (currentIndividual.value.connections_onboarding_completed_at && !isEditMode.value) {
    router.push({ name: 'connecting.individuals' })
    return
  }

  if (isEditMode.value) {
    onboarding.initialize(STEPS.length - 1)
  } else {
    const backendStep = currentIndividual.value.connections_onboarding_current_step || 'personal_information'
    const startStep = BACKEND_STEP_TO_FRONTEND[backendStep] ?? 0
    onboarding.initialize(startStep)
  }

  initCandidateData()
  if (currentIndividual.value.connections_disclosure_accepted_at) {
    disclosuresAgreed.value = true
  }
  hideSkeleton()
})
</script>

<template>
  <!-- Success State (full overlay) -->
  <OnboardingSuccess v-if="showSuccess" />

  <!-- Onboarding -->
  <OnboardingShell
    v-else
    :steps="STEPS"
    :current-step="onboarding.currentStep.value"
    :highest-step-reached="onboarding.highestStepReached.value"
    :is-first-step="onboarding.isFirstStep.value"
    :is-last-step="onboarding.isLastStep.value"
    :step-menu-open="onboarding.stepMenuOpen.value"
    :loading="skeleton"
    :submit-label="isEditMode ? 'Done' : 'Submit'"
    @update:step-menu-open="onboarding.stepMenuOpen.value = $event"
    @continue="onboarding.onClickContinue"
    @back="onboarding.onClickBack"
    @submit="submitOnboarding"
    @step-click="onboarding.onStepClick"
  >
    <template #notification>
      <VNotification />
    </template>

    <template #loading>
      <VShimmerLogo />
    </template>

    <OnboardingAccountForm
      v-if="onboarding.currentStep.value === 0"
      ref="accountForm"
      :individual="currentIndividual"
      :onSubmit="onSubmitPersonalInformation"
    />
    <OnboardingAccreditationForm
      v-if="onboarding.currentStep.value === 1"
      ref="accreditationForm"
      :individual="currentIndividual"
      :onSubmit="onSubmitAccreditation"
    />
    <OnboardingInvestmentForm
      v-if="onboarding.currentStep.value === 2"
      ref="investmentForm"
      :modelValue="candidateData"
      @update:modelValue="(v) => Object.assign(candidateData, v)"
    />
    <OnboardingIndustriesForm
      v-if="onboarding.currentStep.value === 3"
      :modelValue="candidateData"
      @update:modelValue="(v) => Object.assign(candidateData, v)"
    />
    <OnboardingUniversityForm
      v-if="onboarding.currentStep.value === 4"
      :modelValue="candidateData"
      @update:modelValue="(v) => Object.assign(candidateData, v)"
      :universities="universityStore.us_universities"
    />
    <OnboardingDisclosuresForm
      v-if="onboarding.currentStep.value === 5"
      ref="disclosuresForm"
      v-model="disclosuresAgreed"
    />
    <OnboardingReview
      v-if="onboarding.currentStep.value === 6"
      :individual="currentIndividual"
      :candidateData="candidateData"
      :accreditationLabel="accreditationLabel"
      @navigate="onboarding.navigateFromReview"
    />
  </OnboardingShell>
</template>
